import React from "react";
import { useDropzone } from "react-dropzone";
import WaveSurfer from "wavesurfer.js";
import { useSnackbar } from "../toast";
import { ONLY_ONE_FILE_ALLOWED } from "../../helpers/messages";
import { useThemeMode } from "../theme";
import { alpha, Typography } from "@mui/material";

const FileDropZone = ({ files, setFiles, selectedTab }) => {
  const waveformRef = React.useRef(null);
  const wavesurferRef = React.useRef(null);
  const showSnackbar = useSnackbar();
  const { colours } = useThemeMode();

  const onDrop = (acceptedFiles, fileRejections) => {
    const validFiles = acceptedFiles.filter((file) => {
      const validTypes = getAcceptedFileTypes();
      return validTypes.some((type) => file.type.match(type));
    });

    setFiles(
      validFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );

    // when a file rejected.
    if (fileRejections?.length) {
      for (const fileObject of fileRejections) {
        if (fileObject.errors[0].code === "too-many-files") {
          showSnackbar(ONLY_ONE_FILE_ALLOWED, "error");
        } else {
          showSnackbar(fileObject.errors[0]?.message, "error");
        }
        break;
      }
    }

    if (acceptedFiles.length !== validFiles.length) {
      showSnackbar("Some files were rejected because they do not match the required file type.", "error");
    }
  };

  const getAcceptedFileTypes = () => {
    return ["audio/mpeg", "audio/wav", "audio/mp4", "audio/*"];
    // switch (selectedTab) {
    //     case "Upload Audio":
    //       return ["audio/mpeg", "audio/wav", "audio/mp4", "audio/*"];
    //   default:
    //     return ["*/*"];
    // }
  };

  const getDropzoneText = () => {
    return "Drag and drop files here, or click to select files";
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: getAcceptedFileTypes(),
  });

  // Initialize WaveSurfer when we have a file
  React.useEffect(() => {
    if (files.length > 0 && waveformRef.current) {
      // Clean up existing instance if any
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }

      const file = files[0]; // Single file scenario
      if (file.type && file.type.startsWith("audio/")) {
        wavesurferRef.current = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: "#999",
          progressColor: "#555",
          cursorColor: "#333",
          height: 80,
          barWidth: 2,
        });

        wavesurferRef.current.load(file.preview);
      }
    }
  }, [files]);

  return (
    <div
      {...getRootProps()}
      style={{
        border: `2px dashed ${alpha(colours.black, 0.25)}`,
        borderRadius: "8px",
        padding: "20px",
        textAlign: "center",
        cursor: "pointer",
        position: "relative",
        width: "100%",     // Fixed width
        margin: "0 auto",   // Center horizontally within parent
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <input {...getInputProps()} />
      {files.length > 0 ? (
        <div style={{ position: "relative", width: "100%" }}>
          {/* Waveform Container */}
          <div ref={waveformRef} style={{ width: "100%", pointerEvents: "none" }}></div>
          {/* Overlay with filename centered */}
          <Typography
            component="div"
            variant="body2"
            fontWeight={600}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              backgroundColor: alpha(colours.white, 0.7),
              padding: 1,
              width: "100%",
              height: "100%",
              zIndex: 2,
              wordBreak: "break-word",
              overflow: "auto"
            }}
          >
            {files[0].name}
          </Typography>
        </div>
      ) : (
        <div style={{ color: colours.black }}>{getDropzoneText()}</div>
      )}
    </div>
  );
};

export default FileDropZone;
